import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { CurrencyMaskConfig } from '@vsolv/vectors-ui/input';
import { ClaimOverrideRulesService } from '@wsphere/warranties/web';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-approval-settings',
  templateUrl: './claim-approval-settings.component.html',
  standalone: false,
})
export class ClaimApprovalSettingsComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private toastSvc: ToastService,
    private rulesSvc: ClaimOverrideRulesService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() canEdit = false;

  protected loading = true;
  protected saving = false;

  readonly currencyMaskConfig: CurrencyMaskConfig = {
    align: 'left',
    allowNegative: false,
    allowZero: false,
    decimal: '.',
    // Empty turns the rule off.
    nullable: true,
    precision: 2,
    prefix: '$',
    suffix: '',
    thousands: ',',
    inputMode: 1,
  };

  protected form = this.formBuilder.group({
    // In dollars on screen, cents on the API.
    netPayableOver: [null as number | null],
    customerShare: [false],
  });

  ngOnInit(): void {
    void this.load();
  }

  protected async reset() {
    await this.load();
  }

  protected async save() {
    this.saving = true;

    const { netPayableOver, customerShare } = this.form.getRawValue();
    const response = await this.rulesSvc
      .update({
        netPayableOver: netPayableOver ? Math.round(netPayableOver * 100) : null,
        customerShare: !!customerShare,
      })
      .catch(({ error }) => {
        this.toastSvc.show({ type: 'error', title: 'Something went wrong', text: error.message });
        return null;
      });

    if (response) {
      this.toastSvc.show({
        type: 'success',
        title: 'Claim approvals updated',
        text: 'New authorizations and splits use these rules.',
      });
      await this.load();
    }

    this.saving = false;
    this.cdr.markForCheck();
  }

  private async load() {
    this.loading = true;

    const rules = await this.rulesSvc.retrieve().catch(({ error }) => {
      this.toastSvc.show({ type: 'error', title: 'Failed to load claim approvals', text: error.message });
      return null;
    });

    this.form.reset({
      netPayableOver: rules?.netPayableOver ? rules.netPayableOver / 100 : null,
      customerShare: rules?.customerShare ?? false,
    });
    if (!this.canEdit) this.form.disable();

    // Set after awaits, so the view needs marking or the loading text never clears.
    this.loading = false;
    this.cdr.markForCheck();
  }
}
